import { useEffect, useRef, useState } from "react";
import Spinner from "@/components/Spinner";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n.jsx";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { isNativeApp, openSignupSite } from "@/lib/native";
import { loadRememberedCreds, saveRememberedCreds, clearRememberedCreds } from "@/lib/remember";

export default function Login() {
  const nav = useNavigate();
  const location = useLocation();
  const { login, user, loading } = useAuth();
  const { lang } = useI18n();
  const en = lang === "en";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // Default the "Remember me" checkbox to CHECKED so returning users
  // don't need to notice it — the checkbox exists mainly as an
  // opt-out for shared devices.
  const [remember, setRemember] = useState(true);

  // Pre-fill email + password from the last successful login if the
  // user previously ticked "Remember me". Works identically on web,
  // Capacitor iOS, and Capacitor Android — see /lib/remember.js.
  //
  // ALSO pre-fill from `location.state.email` — this is set by the
  // signup flow (IOSOnboarding) when it detects the email already has
  // an account (e.g. user bought on iOS then reinstalled on Android
  // and re-entered the same email in onboarding). Saves the user from
  // re-typing what they just typed 2 seconds ago on the previous
  // screen.
  useEffect(() => {
    const prefillEmail = location?.state?.email;
    if (prefillEmail) {
      setEmail(prefillEmail);
      return;
    }
    const saved = loadRememberedCreds();
    if (saved) {
      setEmail(saved.email);
      setPassword(saved.password);
      setRemember(true);
    }
  }, [location?.state?.email]);

  // Determine the correct landing route based on role + onboarding state.
  // Extracted so we can reuse it for both fresh logins and the auto-login
  // fast-path below.
  // routeFor() consumes calofay_checkout_qs, and both the auto-login effect and
  // submit() fire after a login — only navigate once so the resume query isn't lost.
  const routedRef = useRef(false);
  const goTo = (u) => {
    if (routedRef.current) return;
    routedRef.current = true;
    nav(routeFor(u), { replace: true });
  };
  const routeFor = (u) => {
    if (u.role !== "user") return "/admin";
    // Payment comes before the post-purchase questions (name, height…).
    const paid = ["active", "trialing", "past_due", "cancellation_pending"].includes(u.subscription_status);
    if (!paid) {
      if (isNativeApp()) return "/app"; // SubscriptionGate shows the paywall
      let qs = null;
      try { qs = sessionStorage.getItem("calofay_checkout_qs"); sessionStorage.removeItem("calofay_checkout_qs"); } catch (_) {}
      if (qs !== null) return `/checkout${qs ? `?${qs}` : ""}`;
      return "/app"; // AppShell → /checkout (never paid) or /app/renew (expired)
    }
    if (!u.onboarding_completed) return "/onboarding";
    return "/app";
  };

  // Auto-login: if a valid JWT already exists in localStorage the
  // AuthProvider hydrates `user` on mount. As soon as that lands we
  // bounce the visitor straight to their dashboard so they never have
  // to retype credentials or press "دخول" again.
  useEffect(() => {
    if (!loading && user) {
      goTo(user);
    }
  }, [loading, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      // Persist or wipe creds AFTER a successful login so we never
      // save credentials that turned out to be wrong.
      if (remember) saveRememberedCreds(email, password);
      else clearRememberedCreds();
      // Browsers save the password automatically because the <form> has
      // proper `autoComplete` + `name` attributes below.
      goTo(u);
    } catch (err) {
      toast.error(err.response?.data?.detail || (en ? "Login error" : "خطأ في تسجيل الدخول"));
    } finally {
      setBusy(false);
    }
  };

  // While the AuthProvider is still validating a stored token, show a
  // subtle spinner instead of the login form to avoid a flash of the
  // form for returning users.
  if (loading || user) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center p-5" data-testid="login-auto-loading">
        <Spinner size="md" bare />
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh-bg flex items-center justify-center p-5" dir={en ? "ltr" : "rtl"}>
      <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-card">
        <div className="flex items-center justify-center mb-4">
          <Logo size={48} />
        </div>
        <h1 className="text-2xl font-bold mb-1 text-center">{en ? "Welcome back" : "مرحباً بعودتك"}</h1>
        <p className="text-neutral-500 mb-6 text-center">{en ? "Log in to continue your journey." : "سجل الدخول لمتابعة رحلتك."}</p>
        {/*
          The `name`, `autoComplete` and `id` attributes below are what
          make Chrome / Safari / Firefox offer to save the password
          after a successful login and auto-fill it on the next visit.
          Removing them silently disables password-manager integration.
        */}
        <form onSubmit={submit} className="space-y-4" name="calofay-login" autoComplete="on">
          <label className="block">
            <div className="text-xs text-neutral-500 mb-1.5 font-medium">{en ? "Email" : "البريد الإلكتروني"}</div>
            <input
              id="login-email"
              name="email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-neutral-50 border border-neutral-100 rounded-2xl px-5 py-3.5 focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500 transition-colors"
              data-testid="login-email"
            />
          </label>
          <label className="block">
            <div className="text-xs text-neutral-500 mb-1.5 font-medium">{en ? "Password" : "كلمة المرور"}</div>
            <input
              id="login-password"
              name="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-neutral-50 border border-neutral-100 rounded-2xl px-5 py-3.5 focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500 transition-colors"
              data-testid="login-password"
            />
          </label>
          <label className="flex items-center gap-2.5 cursor-pointer select-none pt-1" data-testid="login-remember-label">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 rounded border-neutral-300 text-brand-500 focus:ring-brand-500/40 cursor-pointer accent-brand-500"
              data-testid="login-remember-checkbox"
            />
            <span className="text-sm text-neutral-700 font-medium">
              {en ? "Remember me on this device" : "تذكرني على هذا الجهاز"}
            </span>
          </label>
          <button type="submit" disabled={busy}
            className="w-full orange-gradient text-white font-semibold py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform disabled:opacity-60"
            data-testid="login-submit-btn">
            {busy ? (en ? "Loading..." : "جاري...") : (en ? "Log in" : "دخول")}
          </button>
        </form>
        <div className="text-center mt-4 space-y-2">
          <button
            type="button"
            onClick={async () => {
              if (!email) return toast.error(en ? "Please enter your email first" : "اكتب بريدك الإلكتروني أولاً");
              try {
                await (await import("@/lib/api")).default.post("/auth/password/request-reset", { email });
                toast.success(en ? "If the email exists, a reset link has been sent" : "إذا كان البريد موجوداً، تم إرسال رابط الإعادة");
              } catch (e) {
                toast.error(en ? "Couldn't send" : "تعذر الإرسال");
              }
            }}
            className="text-sm text-brand-500 hover:underline block w-full"
            data-testid="forgot-password-btn"
          >
            {en ? "Forgot password?" : "نسيت كلمة المرور؟"}
          </button>
        </div>
        <p className="text-center text-sm text-neutral-500 mt-6">
          {/* Signup path.
              WEB: normal quiz signup flow.
              NATIVE (iOS/Android): route to /ios/welcome which owns the
              language picker → welcome slides → signup → quiz → native
              paywall (Apple IAP via RevenueCat, guideline-compliant). */}
          {isNativeApp() ? (
            en
              ? (<>Don't have an account? <Link to="/welcome" className="text-brand-500 font-semibold" data-testid="login-ios-signup-link">Create account</Link></>)
              : (<>ليس لديك حساب؟ <Link to="/welcome" className="text-brand-500 font-semibold" data-testid="login-ios-signup-link">إنشاء حساب</Link></>)
          ) : (
            en
              ? (<>Don't have an account? <Link to="/quiz" className="text-brand-500 font-semibold" data-testid="login-signup-link">Start now</Link></>)
              : (<>ليس لديك حساب؟ <Link to="/quiz" className="text-brand-500 font-semibold" data-testid="login-signup-link">ابدأ الآن</Link></>)
          )}
        </p>
      </div>
    </div>
  );
}
